import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './InfiniteLevel.css';

// Import all your asset pools here
import Lvl1Bg from '../../Assets/Backgrounds/Level1Bg.png';
import Lvl4Bg from '../../Assets/Backgrounds/Stars.png';
import Ship from '../../Assets/Imgs/Ship.png';
import Rock1 from '../../Assets/Imgs/Rock1.png';
import Rock2 from '../../Assets/Imgs/Rock2.png';
import Rock3 from '../../Assets/Imgs/Rock3.png';
import Rock4 from '../../Assets/Imgs/Rock4.png';

import LoseScreen from '../../Components/Layout/LoseScreen';
import WinScreen from '../../Components/Layout/WinScreen';

// --- DIFFICULTY POOLS (Stored in Arrays) ---
const SPEED_POOL = [1.5, 2.5, 4.0, 5.5];       // Speed multipliers
const SPAWN_RATE_POOL = [60, 45, 35, 20];      // Frames between spawns (lower is harder)
const TIME_POOL = [20, 30, 40, 50];            // Seconds required to survive
const SIZE_POOL = [30, 50, 70, 100];           // Base asteroid sizing scales
const BG_POOL = [Lvl1Bg, Lvl4Bg];              // Random Environment Assets
const ASSET_POOL = [Rock1, Rock2, Rock3, Rock4];

const InfiniteLevel = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    // Parse what level number the player is currently on (e.g., Level 5, 6, 7...)
    const currentLevelNum = parseInt(searchParams.get('level')) || 5;

    // --- AUTOMATED CONFIGURATION GENERATOR ---
    // This runs once when the level mounts to create a completely random combination
    const [levelConfig] = useState(() => {
        const randomBg = BG_POOL[Math.floor(Math.random() * BG_POOL.length)];
        
        // Random selection from the defined difficulty factor arrays
        const chosenSpeed = SPEED_POOL[Math.floor(Math.random() * SPEED_POOL.length)];
        const chosenSpawnRate = SPAWN_RATE_POOL[Math.floor(Math.random() * SPAWN_RATE_POOL.length)];
        const chosenTime = TIME_POOL[Math.floor(Math.random() * TIME_POOL.length)];
        const chosenSize = SIZE_POOL[Math.floor(Math.random() * SIZE_POOL.length)];

        return {
            background: randomBg,
            asteroidSpeed: chosenSpeed,
            spawnRate: chosenSpawnRate,
            targetTime: chosenTime,
            baseSize: chosenSize
        };
    });

    // Ship Layout
    const SHIP_WIDTH = 172;
    const SHIP_HEIGHT = 187;
    const MOVE_SPEED = 7;
    const SHIP_BOTTOM = 40;
    const INITIAL_SHIP_X = window.innerWidth / 2 - SHIP_WIDTH / 2;

    // States
    const [shipX, setShipX] = useState(INITIAL_SHIP_X);
    const [asteroids, setAsteroids] = useState([]);
    const [isDead, setIsDead] = useState(false);
    const [isWon, setIsWon] = useState(false);

    // Physics Loop refs
    const shipXRef = useRef(INITIAL_SHIP_X);
    const keysPressed = useRef({});
    const spawnTimer = useRef(0);
    const asteroidIdCounter = useRef(0);
    const survivalTimer = useRef(0);

    const handleRetry = () => {
        keysPressed.current = {};
        spawnTimer.current = 0;
        survivalTimer.current = 0;
        shipXRef.current = INITIAL_SHIP_X;
        setShipX(INITIAL_SHIP_X);
        setAsteroids([]);
        setIsDead(false);
        setIsWon(false);
    };

    const handleNextLevel = () => {
        // Automatically redirects them to the exact same component page, 
        // but increments the level parameter, causing a fresh re-roll of assets & difficulty!
        navigate(`/infinite?level=${currentLevelNum + 1}`);
        window.location.reload(); // Quick reset to wipe the canvas state clean
    };

    useEffect(() => {
        const handleKeyDown = (e) => { keysPressed.current[e.key.toLowerCase()] = true; };
        const handleKeyUp = (e) => { keysPressed.current[e.key.toLowerCase()] = false; };
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);

        let animationFrameId;

        const updateGame = () => {
            
            if (isDead || isWon) return;

            // 1. Evaluate Dynamic Survival Timer Condition
            survivalTimer.current += 1 / 60;
            if (survivalTimer.current >= levelConfig.targetTime) {
                setIsWon(true);
                return;
            }

            // 2. Spaceship Locomotion Control
            let nextX = shipXRef.current;
            if (keysPressed.current['a'] || keysPressed.current['arrowleft']) nextX -= MOVE_SPEED;
            if (keysPressed.current['d'] || keysPressed.current['arrowright']) nextX += MOVE_SPEED;
            if (nextX < 0) nextX = 0;
            if (nextX > window.innerWidth - SHIP_WIDTH) nextX = window.innerWidth - SHIP_WIDTH;
            shipXRef.current = nextX;
            setShipX(nextX);

            // 3. Automated Asset Spawning Logic using dynamic settings
            spawnTimer.current += 1;
            if (spawnTimer.current >= levelConfig.spawnRate) {
                const randomImg = ASSET_POOL[Math.floor(Math.random() * ASSET_POOL.length)];
                const dynamicSize = Math.floor(Math.random() * 30) + levelConfig.baseSize; 
                const randomX = Math.random() * (window.innerWidth - dynamicSize);

                const newAsteroid = {
                    id: asteroidIdCounter.current++,
                    img: randomImg,
                    x: randomX,
                    y: -dynamicSize,
                    size: dynamicSize,
                    speed: levelConfig.asteroidSpeed + (Math.random() * 2) // adds a tiny bit of variance
                };

                setAsteroids((prev) => [...prev, newAsteroid]);
                spawnTimer.current = 0;
            }

            // 4. Hitbox Collision Loop Calculations
            setAsteroids((prevAsteroids) => {
                const updated = [];
                const shipTop = window.innerHeight - SHIP_BOTTOM - SHIP_HEIGHT;
                const shipBottom = window.innerHeight - SHIP_BOTTOM;
                const currentShipX = shipXRef.current;

                for (let i = 0; i < prevAsteroids.length; i++) {
                    const rock = prevAsteroids[i];
                    const nextY = rock.y + rock.speed;

                    const overlapX = (currentShipX + SHIP_WIDTH > rock.x) && (currentShipX < rock.x + rock.size);
                    const overlapY = (shipBottom > nextY) && (shipTop < nextY + rock.size);

                    if (overlapX && overlapY) setIsDead(true);

                    if (nextY < window.innerHeight) {
                        updated.push({ ...rock, y: nextY });
                    }
                }
                return updated;
            });

            animationFrameId = requestAnimationFrame(updateGame);
        };
        

        animationFrameId = requestAnimationFrame(updateGame);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            cancelAnimationFrame(animationFrameId);
        };
    }, [isDead, isWon, levelConfig]);

    useEffect(() => {
        setIsWon(false);
        setIsDead(false);
        setAsteroids([]);
        spawnTimer.current = 0;
        survivalTimer.current = 0;
        
        // Reset ship back to the center of the screen
        shipXRef.current = window.innerWidth / 2 - SHIP_WIDTH / 2;
        setShipX(shipXRef.current);
    }, [currentLevelNum]);

    return (
        <main className='Level4Main' key={currentLevelNum}>
            {/* Heads-Up Display showing the dynamic requirements */}
            <div className="InfiniteHUD">
                <h2>LEVEL {currentLevelNum} (Procedural Mode)</h2>
                <div>TIME LEFT: {Math.max(0, Math.ceil(levelConfig.targetTime - survivalTimer.current))}s</div>
                <div>ROCK SPEED: {levelConfig.asteroidSpeed.toFixed(1)}</div>
            </div>

            <div className='Lvl4Bg'>
                <img src={levelConfig.background} alt="Dynamic Environment" />
            </div>

            {asteroids.map((rock) => (
                <div key={rock.id} className='Asteroid' style={{ left: `${rock.x}px`, top: `${rock.y}px`, width: `${rock.size}px`, height: `${rock.size}px` }}>
                    <img src={rock.img} alt="Obstacle" style={{ width: '100%', height: '100%' }} />
                </div>
            ))}

            <div className='Ship' style={{ left: `${shipX}px`, width: `${SHIP_WIDTH}px`, height: `${SHIP_HEIGHT}px`, bottom: `${SHIP_BOTTOM}px` }}>
                <img src={Ship} alt="Spaceship" style={{ width: '100%', height: '100%' }} />
            </div>

            {isDead && (
                <div className="LoseScreenContainer">
                    <LoseScreen RetryClick={handleRetry} />
                </div>
            )}

            {isWon && (
                <div className="WinScreenWrapper">
                    <WinScreen 
                        RetryClick={handleRetry} 
                        LevelPath={`/infinite?level=${currentLevelNum + 1}`} 
                    />
                </div>
            )}
        </main>
    );
};

export default InfiniteLevel;